import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Loader2,
  UploadCloud,
  CheckCircle2,
  Sparkles,
  ArrowLeft,
  FileText,
  Hash,
  Copy,
  Check,
  ClipboardList,
} from "lucide-react";
import OnlineApplicationForm from "@/components/OnlineApplicationForm";
import { useEmployerBrand, BrandLogo, PoweredByBadge } from "@/lib/brand";

const H_LOGO_URL =
  "/brand/hireloop-h.png";

export default function PublicApply() {
  const { employerId, jobId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [appRef, setAppRef] = useState("");
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState("resume"); // 'resume' | 'form'
  const { brand } = useEmployerBrand(employerId);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/public/careers/${employerId}/jobs/${jobId}`)
      .then(({ data }) => !cancelled && setData(data))
      .catch((e) =>
        !cancelled && setErr(e?.response?.data?.detail || "Could not load role")
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [employerId, jobId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please attach your résumé");
      return;
    }
    const allowed = ["pdf", "docx", "txt"];
    const ext = (file.name || "").toLowerCase().split(".").pop();
    if (!allowed.includes(ext)) {
      toast.error("Only PDF, DOCX, or TXT files are supported");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File must be under 5 MB");
      return;
    }
    setSubmitting(true);
    const form = new FormData();
    form.append("file", file);
    if (name) form.append("applicant_name", name);
    if (email) form.append("applicant_email", email);
    try {
      const { data } = await api.post(
        `/public/careers/${employerId}/jobs/${jobId}/apply`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setAppRef(data?.application_ref || "");
      setDone(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Application could not be submitted"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const copyRef = async () => {
    if (!appRef) return;
    try {
      await navigator.clipboard.writeText(appRef);
      setCopied(true);
      toast.success("Reference number copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy — please write down your reference number");
    }
  };

  if (loading) {
    return (
      <main className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
      </main>
    );
  }
  if (err) {
    return (
      <main className="mx-auto max-w-md px-6 py-16 text-center">
        <h1 className="font-heading text-2xl font-semibold">Role unavailable</h1>
        <p className="mt-2 text-sm text-black">{err}</p>
        <Link
          to={`/c/${employerId}`}
          className="mt-6 inline-block text-sm font-medium text-blue-700 hover:underline"
        >
          ← Back to open roles
        </Link>
      </main>
    );
  }
  if (!data) return null;

  const { employer, job } = data;
  const companyLabel = employer.company || employer.name || "this team";

  return (
    <main
      data-testid="public-apply-page"
      className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-12"
    >
      <Link
        to={`/c/${employerId}`}
        className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All open roles at {companyLabel}
      </Link>

      <header className="mt-6 flex items-center gap-3">
        {brand?.logo_url ? (
          <BrandLogo
            brand={brand}
            className="h-12 w-auto max-w-[160px] object-contain"
            alt={brand.company || companyLabel}
          />
        ) : (
          !brand?.hide_powered_by && (
            <img
              src={H_LOGO_URL}
              alt="Hireloop PRO"
              className="h-10 w-10 object-contain"
              style={{ mixBlendMode: "multiply" }}
            />
          )
        )}
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-blue-700">
          Apply · {companyLabel}
        </div>
      </header>

      <h1 className="mt-6 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
        {job.title}
      </h1>
      {job.description && (
        <p className="mt-3 whitespace-pre-line text-sm text-black">
          {job.description}
        </p>
      )}

      {done ? (
        <div
          data-testid="public-apply-success"
          className="mt-10 flex flex-col items-center gap-4 border border-emerald-200 bg-emerald-50 px-6 py-10 text-center"
        >
          <CheckCircle2 className="h-10 w-10 text-emerald-700" />
          <h2 className="font-heading text-xl font-semibold text-emerald-900">
            Application received
          </h2>
          <p className="max-w-md text-sm text-emerald-900">
            Thanks — your résumé is in front of the {companyLabel} team. If
            you're a strong match they'll reach out by email with next steps,
            usually within a few business days.
          </p>

          {appRef && (
            <div
              data-testid="public-apply-ref-card"
              className="mt-2 w-full max-w-sm border border-emerald-300 bg-white px-5 py-4 text-left"
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-700">
                <Hash className="mr-1 inline h-3 w-3 -translate-y-0.5" />
                Your application reference
              </div>
              <div className="mt-1.5 flex items-center justify-between gap-3">
                <span
                  data-testid="public-apply-ref-value"
                  className="font-mono text-2xl font-semibold tracking-wide text-emerald-950"
                >
                  {appRef}
                </span>
                <button
                  type="button"
                  data-testid="public-apply-ref-copy-btn"
                  onClick={copyRef}
                  className="inline-flex items-center gap-1 border border-emerald-300 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-800 hover:bg-emerald-50"
                >
                  {copied ? (
                    <>
                      <Check className="h-3 w-3" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3" /> Copy
                    </>
                  )}
                </button>
              </div>
              <div className="mt-2 text-[11px] leading-relaxed text-emerald-800">
                Please save this number. Mention it in any follow-up email so
                the {companyLabel} team can locate your application instantly.
              </div>
              <Link
                to="/application-status"
                data-testid="public-apply-status-link"
                className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-800 hover:underline"
              >
                Check your application status anytime →
              </Link>
            </div>
          )}

          {!brand?.hide_powered_by && (
            <p className="mt-1 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-emerald-700">
              <Sparkles className="h-3 w-3" /> Reviewed with Hireloop PRO
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Apply-method toggle */}
          <div
            data-testid="apply-mode-toggle"
            className="mt-8 inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white p-1 shadow-sm"
          >
            <button
              type="button"
              data-testid="apply-mode-resume"
              onClick={() => setMode("resume")}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] transition ${
                mode === "resume"
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <FileText className="h-3 w-3" />
              Upload résumé
            </button>
            <button
              type="button"
              data-testid="apply-mode-form"
              onClick={() => setMode("form")}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] transition ${
                mode === "form"
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <ClipboardList className="h-3 w-3" />
              Fill out application
            </button>
          </div>

          {mode === "form" ? (
            <div className="mt-6">
              <OnlineApplicationForm
                employerId={employerId}
                jobId={jobId}
                onSuccess={(data) => {
                  setAppRef(data?.application_ref || "");
                  setDone(true);
                }}
              />
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="mt-6 space-y-5 border border-slate-200 bg-white p-6"
            >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label
                htmlFor="apply-name"
                className="text-xs font-bold uppercase tracking-[0.2em] text-black"
              >
                Your name
              </Label>
              <Input
                id="apply-name"
                data-testid="public-apply-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 rounded-sm border-slate-300"
                placeholder="Alex Carter"
              />
            </div>
            <div>
              <Label
                htmlFor="apply-email"
                className="text-xs font-bold uppercase tracking-[0.2em] text-black"
              >
                Email
              </Label>
              <Input
                id="apply-email"
                data-testid="public-apply-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-sm border-slate-300"
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-black">
              Résumé (PDF, DOCX, or TXT — under 5 MB)
            </Label>
            <label
              htmlFor="apply-file"
              className="mt-2 flex cursor-pointer items-center gap-3 border border-dashed border-slate-300 bg-slate-50 px-4 py-6 hover:bg-slate-100"
            >
              <UploadCloud className="h-6 w-6 text-blue-700" />
              <div className="flex-1 text-sm">
                {file ? (
                  <span
                    data-testid="public-apply-file-label"
                    className="flex items-center gap-2 font-medium text-black"
                  >
                    <FileText className="h-4 w-4 text-slate-500" />
                    {file.name}{" "}
                    <span className="text-xs text-slate-500">
                      ({(file.size / 1024).toFixed(0)} KB)
                    </span>
                  </span>
                ) : (
                  <span className="text-slate-600">
                    Drop a file here or click to browse
                  </span>
                )}
              </div>
            </label>
            <input
              id="apply-file"
              data-testid="public-apply-file-input"
              type="file"
              accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
            />
          </div>

          <Button
            type="submit"
            data-testid="public-apply-submit-btn"
            disabled={submitting || !file}
            className="w-full rounded-sm bg-blue-700 py-6 hover:bg-blue-600"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit application
          </Button>

          <p className="text-[11px] text-slate-500">
            By submitting, you allow {companyLabel} to review your résumé
            {brand?.hide_powered_by ? "" : " and score it with Hireloop PRO's AI"}.
            You can ask for your data to be deleted any time.
          </p>
        </form>
          )}
        </>
      )}
      <div className="mt-10 flex items-center justify-end">
        <PoweredByBadge brand={brand} />
      </div>
    </main>
  );
}
